import { useEffect } from 'react'
import { useAuthStore } from '../auth/store'
import {
  enterReadonlyFallback,
  isReadonlyFallbackActive,
} from '../store/readonlyMode'
import {
  checkElectricAvailableOnce,
  hasNotesLocalCacheBaseline,
  isElectricAvailable,
  onElectricStatusChange,
  startReadonlyRecoveryProbe,
  waitForNotesHydration,
} from '../store/collections'

/**
 * ★ 离线/不稳定自动只读（统一策略入口，App 层挂载一次）
 *
 * 触发信号（满足任一即启动确认流程，覆盖启动期与会话中途两个场景）：
 *   1. 设备离线（navigator.onLine=false）
 *   2. 数据服务不可达（shape 错误确认 / 健康巡检失败 → electricAvailable=false）
 *
 * ★ 证据式确认（防误报，行业惯例：debounce 宽限 + 主动探测验证）：
 *   单次负向信号在弱网下（室外、WiFi↔蜂窝切换）频繁出现且多在数秒内自愈
 *   （ShapeStream 自带指数退避重试），立即降级会误弹只读提示。进入只读前
 *   必须验证离线事实：
 *   ① 宽限期 CONFIRM_DELAY_MS：负向信号需持续存在——窗口内自愈（evaluate
 *     复查两信号均健康）则取消确认；
 *   ② 主动探测：宽限期满后请求 /api/sync/health 验证真实连通性
 *     （navigator.onLine 只反映网卡状态，不足以判定）：
 *     - 探测成功 → 网络实际可用（误报），取消；
 *     - 探测失败 且 设备报离线 → 双重证据，确认离线；
 *     - 探测失败 但 设备报在线 → RECHECK_DELAY_MS 后复测一次，连续失败
 *       才确认"服务不可达"（防单次服务端抖动）。
 *   ③ 本地有数据（HWM 基线 > 0，或水合行数 > 0）——新装无缓存不进入
 *     （无数据可看，保持骨架屏完全阻塞等待网络）。
 *
 * 进入后：放行框架（SujiPage/CRDTPage 只读豁免分支）、TipTap 不可编辑、
 *   写入口拦截、全局横幅常驻。
 * 退出：云端对齐（markNotesCloudAligned / 恢复探测 startReadonlyRecoveryProbe）
 *   自动恢复完整功能，零用户操作。
 */

/** 宽限期：负向信号需持续的时长（覆盖典型网络切换/瞬断自愈窗口 2-6s） */
const CONFIRM_DELAY_MS = 8000
/** 设备报在线但探测失败时的复测间隔（单次失败可能是服务端抖动） */
const RECHECK_DELAY_MS = 5000

export function useAutoReadonlyFallback(): void {
  const user = useAuthStore((s) => s.user)

  useEffect(() => {
    const userId = user?.id
    if (!userId) return
    let cancelled = false
    // 确认流程状态：pending=true 表示宽限计时器在跑（防重复启动）；
    // epoch 仅在"信号恢复/组件卸载"时递增，作废在途流程的全部迟到结果
    let confirmPending = false
    let confirmEpoch = 0

    const deviceOffline = () =>
      typeof navigator !== 'undefined' && !navigator.onLine

    const signalsDown = () => deviceOffline() || !isElectricAvailable()

    const enterIfLocalData = async (
      epoch: number,
      reason: 'offline-fallback' | 'unstable-fallback'
    ) => {
      // 本地数据判定：HWM 基线（localStorage，不依赖集合初始化时序）；
      // 无基线（新装）再等水合计数兜底（HWM 滞后场景）
      let hasLocalData = hasNotesLocalCacheBaseline(userId)
      if (!hasLocalData) {
        try {
          const count = await waitForNotesHydration()
          hasLocalData = count > 0
        } catch {
          /* 集合未初始化：无数据，不进入 */
        }
      }
      // 进入前复查：确认流程未被取消（水合等待期间网络可能已自愈）
      if (
        !cancelled &&
        epoch === confirmEpoch &&
        hasLocalData &&
        signalsDown() &&
        !isReadonlyFallbackActive()
      ) {
        enterReadonlyFallback(reason)
      }
    }

    /** 宽限期满后的离线事实验证（主动探测） */
    const verifyOffline = async (epoch: number) => {
      // 宽限计时已触发：解除 pending 锁——本轮探测/复测期间信号若再次波动，
      // 可重新走宽限期（epoch 保证旧周期的迟到结果不会误入新周期）
      confirmPending = false
      if (isReadonlyFallbackActive()) return
      // 复查：窗口内自愈则取消（不发起无谓请求）
      if (!signalsDown()) return
      const offlineNow = deviceOffline()
      let reachable = false
      try {
        reachable = await checkElectricAvailableOnce()
      } catch {
        reachable = false
      }
      if (cancelled || epoch !== confirmEpoch) return
      if (reachable) {
        // 网络实际可用（navigator.onLine 误报或瞬时错误已自愈）——不降级
        return
      }
      if (offlineNow) {
        // 双重证据：设备报离线 + 探测失败 → 确认离线
        await enterIfLocalData(epoch, 'offline-fallback')
        return
      }
      // 设备报在线但服务探测失败：复测一次，连续失败才降级
      setTimeout(() => {
        if (cancelled || epoch !== confirmEpoch) return
        void (async () => {
          let reachableAgain = false
          try {
            reachableAgain = await checkElectricAvailableOnce()
          } catch {
            reachableAgain = false
          }
          if (cancelled || epoch !== confirmEpoch) return
          if (reachableAgain) return
          await enterIfLocalData(epoch, 'unstable-fallback')
        })()
      }, RECHECK_DELAY_MS)
    }

    const evaluate = () => {
      if (isReadonlyFallbackActive()) return
      if (!signalsDown()) {
        // 两信号均健康：取消进行中的确认流程（瞬断已自愈），作废在途探测
        if (confirmPending) {
          confirmPending = false
          confirmEpoch++
        }
        return
      }
      // ★ 设备离线即时进入（2026-09-19 加速，"后台断网切前台满屏网络不通"实测
      // 修复）：掉线方向的 navigator.onLine 是可靠信号（W3C/MDN：不可靠的是
      // 恢复方向——浏览器无法保证检测到恢复；掉线由系统网络栈确认）——无需
      // 8s 宽限与探测验证，立即进入只读确认（enterIfLocalData 内部复查本地
      // 有数据+仍离线）。误报代价=只读横幅短暂闪现+探测自动退出（探测请求
      // 为单一事实源，网络实际可用即退出）；死等代价=满屏误导性"网络不通"
      // + 本地数据不可见——快速只读是安全方向。原"启动期加速"要求云端未
      // 对齐（!isNotesCloudAligned），会话中已对齐时断网走 8s 宽限——宽限期
      // 内骨架屏持续"网络不通"与事实不符（本地数据可看），故统一即时进入。
      if (deviceOffline()) {
        const epoch = confirmEpoch
        void enterIfLocalData(epoch, 'offline-fallback')
        return
      }
      if (confirmPending) return // 已在确认流程中（宽限期不因新信号重置）
      confirmPending = true
      const epoch = confirmEpoch
      // 宽限期：给瞬断留自愈窗口，期满再主动探测验证
      setTimeout(() => {
        if (cancelled || epoch !== confirmEpoch) return
        void verifyOffline(epoch)
      }, CONFIRM_DELAY_MS)
    }

    void evaluate()

    const onNetChange = () => {
      void evaluate()
      // ★ 网络事件时主动拉起恢复探测（online 与 offline 都拉起——探测请求为
      //   单一事实源自判网络，事件仅是"提前拉起"的触发器而非门禁）：
      //   ShapeStream 对网络层失败静默重试，electricAvailable 可能从未变
      //   false（无恢复广播）——只读退出全靠此触发 + 只读期间的 2s 心跳探测
      if (isReadonlyFallbackActive()) {
        startReadonlyRecoveryProbe()
      }
    }
    window.addEventListener('offline', onNetChange)
    window.addEventListener('online', onNetChange)
    // ★ 前台切换触发（2026-09-19）：后台期间断网，offline 事件可能因页面
    //   节流丢失派发——切回前台时复查信号（navigator.onLine 已由系统刷新），
    //   确保离线只读进入与恢复探测拉起都有触发源
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onNetChange()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    const unsubscribeElectric = onElectricStatusChange(() => void evaluate())
    return () => {
      cancelled = true
      confirmEpoch++
      window.removeEventListener('offline', onNetChange)
      window.removeEventListener('online', onNetChange)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      unsubscribeElectric()
    }
  }, [user?.id])
}
